// MapLibre mounts custom markers as plain DOM elements. Because selection is
// handled by our own button click listener, we need to ignore pointer-originated
// clicks that end far away from where the press started, otherwise a drag/pan can
// still open stop details after the pointer is released.
const MAX_MARKER_CLICK_OFFSET_PX = 6;

interface MarkerPressPosition {
  clientX: number;
  clientY: number;
}

interface CreateSelectableMarkerRootOptions {
  markerId: string;
  ariaLabel: string;
  className: string;
  onSelect?: (markerId: string) => void;
}

function hasMovedBeyondClickTolerance(
  pressPosition: MarkerPressPosition,
  clickEvent: MouseEvent,
): boolean {
  const deltaX = clickEvent.clientX - pressPosition.clientX;
  const deltaY = clickEvent.clientY - pressPosition.clientY;

  return Math.hypot(deltaX, deltaY) > MAX_MARKER_CLICK_OFFSET_PX;
}

export function createSelectableMarkerRoot({
  markerId,
  ariaLabel,
  className,
  onSelect,
}: CreateSelectableMarkerRootOptions): HTMLButtonElement | HTMLDivElement {
  const root = document.createElement(onSelect ? 'button' : 'div');

  if (!(root instanceof HTMLButtonElement)) {
    root.className = className;
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('aria-label', ariaLabel);
    return root;
  }

  let pressPosition: MarkerPressPosition | null = null;

  root.type = 'button';
  root.className = className;
  root.setAttribute('aria-label', ariaLabel);

  root.addEventListener('mousedown', (event) => {
    if (event.button !== 0) {
      return;
    }

    pressPosition = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
  });

  root.addEventListener('blur', () => {
    pressPosition = null;
  });

  root.addEventListener('click', (event) => {
    const previousPressPosition = pressPosition;
    pressPosition = null;

    // Keep keyboard activation working. Enter/Space-triggered button clicks do
    // not have pointer coordinates and report detail 0, so only suppress clicks
    // that clearly came from a pointer interaction.
    if (
      previousPressPosition &&
      event.detail > 0 &&
      hasMovedBeyondClickTolerance(previousPressPosition, event)
    ) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onSelect?.(markerId);
  });

  return root;
}
